import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  LinearProgress,
  Snackbar,
} from "@mui/material";
import { Search, Send, Refresh, Close, Sync } from "@mui/icons-material";
import { dashboardAPI, settingsAPI } from "../../services/api";
import { stripHtml, sanitizeHtml } from "../../utils/htmlUtils";

// Strip HTML from work item descriptions to reduce payload size
const stripHtmlFromWorkItem = (workItem) => {
  const cleaned = { ...workItem };
  if (cleaned.description) {
    cleaned.description = stripHtml(cleaned.description);
  }
  return cleaned;
};

const BacklogView = ({ activeConfigurationId, onWorkItemsSubmitted }) => {
  // Helper function to get localStorage key for filters
  const getStorageKey = (configId) =>
    `backlog_filters_${configId || "default"}`;

  // Load saved filters from localStorage
  const loadSavedFilters = (configId) => {
    try {
      const saved = localStorage.getItem(getStorageKey(configId));
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          searchTerm: parsed.searchTerm || "",
          columnFilters: parsed.columnFilters || {
            id: null,
            type: null,
            status: null,
            assigned_to: null,
            iteration: null,
            connection: null,
          },
          excludeClosedRemoved:
            parsed.excludeClosedRemoved !== undefined
              ? parsed.excludeClosedRemoved
              : true,
          page: parsed.page || 1,
          pageSize: parsed.pageSize || 20,
        };
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
    }
    return {
      searchTerm: "",
      columnFilters: {
        id: null,
        type: null,
        status: null,
        assigned_to: null,
        iteration: null,
        connection: null,
      },
      excludeClosedRemoved: true,
      page: 1,
      pageSize: 20,
    };
  };

  // Save filters to localStorage
  const saveFilters = (configId, filters) => {
    try {
      localStorage.setItem(
        getStorageKey(configId),
        JSON.stringify({
          searchTerm: filters.searchTerm,
          columnFilters: filters.columnFilters,
          excludeClosedRemoved: filters.excludeClosedRemoved,
          page: filters.page,
          pageSize: filters.pageSize,
        }),
      );
    } catch (error) {
      console.error("Error saving filters:", error);
    }
  };

  const [workItems, setWorkItems] = useState([]);
  const [filteredWorkItems, setFilteredWorkItems] = useState([]);
  const [paginatedWorkItems, setPaginatedWorkItems] = useState([]);
  const [selectedWorkItems, setSelectedWorkItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [hasConnections, setHasConnections] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    id: null,
    type: null,
    status: null,
    assigned_to: null,
    iteration: null,
    connection: null,
  });
  const [excludeClosedRemoved, setExcludeClosedRemoved] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(null); // { total, processed, status }
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const isInitialLoad = useRef(true);
  const prevFiltersRef = useRef({
    searchTerm: "",
    columnFilters: {},
    excludeClosedRemoved: true,
  });

  // Get unique values for each filter column
  const filterOptions = useMemo(() => {
    const options = {
      id: [],
      type: [],
      status: [],
      assigned_to: [],
      iteration: [],
      connection: [],
    };

    workItems.forEach((item) => {
      if (item.task_id && !options.id.includes(item.task_id)) {
        options.id.push(item.task_id);
      }
      if (item.work_item_type && !options.type.includes(item.work_item_type)) {
        options.type.push(item.work_item_type);
      }
      if (item.status && !options.status.includes(item.status)) {
        options.status.push(item.status);
      }
      if (item.assigned_to && !options.assigned_to.includes(item.assigned_to)) {
        options.assigned_to.push(item.assigned_to);
      }
      if (item.iteration_path) {
        const iterationName = item.iteration_path.split("\\").pop();
        if (!options.iteration.includes(iterationName)) {
          options.iteration.push(iterationName);
        }
      }
      if (
        item.connection_name &&
        !options.connection.includes(item.connection_name)
      ) {
        options.connection.push(item.connection_name);
      }
    });

    // Sort all options
    Object.keys(options).forEach((key) => {
      options[key].sort();
    });

    return options;
  }, [workItems]);

  // Load saved filters when configuration changes
  useEffect(() => {
    if (activeConfigurationId) {
      isInitialLoad.current = true;
      const saved = loadSavedFilters(activeConfigurationId);
      setSearchTerm(saved.searchTerm);
      setColumnFilters(saved.columnFilters);
      setExcludeClosedRemoved(saved.excludeClosedRemoved);
      setPage(saved.page);
      setPageSize(saved.pageSize);
      prevFiltersRef.current = {
        searchTerm: saved.searchTerm,
        columnFilters: saved.columnFilters,
        excludeClosedRemoved: saved.excludeClosedRemoved,
      };
      loadWorkItems();

      // Check for ongoing submission status
      const submissionKey = `backlog_submission_${activeConfigurationId}`;
      try {
        const savedSubmission = localStorage.getItem(submissionKey);
        if (savedSubmission) {
          const parsed = JSON.parse(savedSubmission);
          // Only restore if it's recent (within last 5 minutes)
          const submissionTime = new Date(parsed.timestamp);
          const now = new Date();
          if ((now - submissionTime) / 1000 / 60 < 5) {
            setSubmissionProgress(parsed);
            setSubmitting(true);
          } else {
            localStorage.removeItem(submissionKey);
          }
        }
      } catch (e) {
        console.error("Error loading submission status:", e);
      }
    } else {
      setWorkItems([]);
      setFilteredWorkItems([]);
      setPaginatedWorkItems([]);
    }
  }, [activeConfigurationId]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (activeConfigurationId) {
      saveFilters(activeConfigurationId, {
        searchTerm,
        columnFilters,
        excludeClosedRemoved,
        page,
        pageSize,
      });
    }
  }, [
    activeConfigurationId,
    searchTerm,
    columnFilters,
    excludeClosedRemoved,
    page,
    pageSize,
  ]);

  useEffect(() => {
    // Filter work items based on search term and column filters
    if (workItems.length === 0) {
      setFilteredWorkItems([]);
      setPaginatedWorkItems([]);
      return;
    }

    let filtered = [...workItems];

    // Apply search term filter
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(term) ||
          item.description?.toLowerCase().includes(term) ||
          item.task_id?.toLowerCase().includes(term) ||
          item.status?.toLowerCase().includes(term) ||
          item.work_item_type?.toLowerCase().includes(term) ||
          item.assigned_to?.toLowerCase().includes(term) ||
          item.iteration_path?.toLowerCase().includes(term),
      );
    }

    // Apply default exclusion of Closed/Removed statuses (unless user explicitly selects them)
    if (excludeClosedRemoved && !columnFilters.status) {
      filtered = filtered.filter((item) => {
        const statusLower = item.status?.toLowerCase() || "";
        return (
          !statusLower.includes("closed") && !statusLower.includes("removed")
        );
      });
    }

    // Apply column filters (only if filter value is not null/empty)
    if (columnFilters.id) {
      filtered = filtered.filter((item) => item.task_id === columnFilters.id);
    }
    if (columnFilters.type) {
      filtered = filtered.filter(
        (item) => item.work_item_type === columnFilters.type,
      );
    }
    if (columnFilters.status) {
      filtered = filtered.filter(
        (item) => item.status === columnFilters.status,
      );
    }
    if (columnFilters.assigned_to) {
      filtered = filtered.filter(
        (item) => item.assigned_to === columnFilters.assigned_to,
      );
    }
    if (columnFilters.iteration) {
      filtered = filtered.filter((item) => {
        const iterationName = item.iteration_path?.split("\\").pop();
        return iterationName === columnFilters.iteration;
      });
    }
    if (columnFilters.connection) {
      filtered = filtered.filter(
        (item) => item.connection_name === columnFilters.connection,
      );
    }

    console.log(
      "[BacklogView] Filtered work items:",
      filtered.length,
      "from",
      workItems.length,
    );
    setFilteredWorkItems(filtered);

    // Reset to page 1 if filters actually changed (not on initial load)
    const filtersChanged =
      prevFiltersRef.current.searchTerm !== searchTerm ||
      JSON.stringify(prevFiltersRef.current.columnFilters) !==
        JSON.stringify(columnFilters) ||
      prevFiltersRef.current.excludeClosedRemoved !== excludeClosedRemoved;

    if (!isInitialLoad.current && filtersChanged) {
      setPage(1);
    }

    // Update refs
    prevFiltersRef.current = {
      searchTerm,
      columnFilters,
      excludeClosedRemoved,
    };
    isInitialLoad.current = false;
  }, [searchTerm, columnFilters, workItems, excludeClosedRemoved]);

  // Paginate filtered items
  useEffect(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPaginatedWorkItems(filteredWorkItems.slice(startIndex, endIndex));
  }, [filteredWorkItems, page, pageSize]);

  const loadWorkItems = async () => {
    if (!activeConfigurationId) return;

    setLoading(true);
    setError("");
    setHasConnections(false);
    setEmptyMessage("");
    try {
      const response = await dashboardAPI.getWorkItems(activeConfigurationId);

      // Handle new response format (object with workItems) or old format (array for backward compatibility)
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data && response.data.workItems) {
        items = response.data.workItems || [];
        if (response.data.hasConnections && items.length === 0) {
          setHasConnections(true);
          setEmptyMessage(
            response.data.message ||
              "No work items found. Please sync tasks from your DevOps connections in Settings.",
          );
        }
      }

      console.log("[BacklogView] Loaded work items:", items.length);
      setWorkItems(items);
      // Don't set filteredWorkItems here - let the useEffect handle filtering
    } catch (err) {
      console.error("[BacklogView] Error loading work items:", err);
      setError(
        err.response?.data?.error ||
          "Failed to load work items. Please ensure DevOps connections are configured and verified.",
      );
      setWorkItems([]);
      setFilteredWorkItems([]);
      setPaginatedWorkItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = new Set(paginatedWorkItems.map((item) => item.task_id));
      setSelectedWorkItems(allIds);
    } else {
      setSelectedWorkItems(new Set());
    }
  };

  const handleSelectItem = (taskId) => {
    const newSelected = new Set(selectedWorkItems);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedWorkItems(newSelected);
  };

  const handleTitleClick = (item) => {
    setSelectedWorkItem(item);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedWorkItem(null);
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
    // If user explicitly selects a status, disable the default exclusion
    if (column === "status" && value) {
      setExcludeClosedRemoved(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Clear selection when changing pages
    setSelectedWorkItems(new Set());
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
    setSelectedWorkItems(new Set()); // Clear selection
  };

  const handleSyncFromDevOps = async () => {
    if (!activeConfigurationId) return;

    setSyncing(true);
    setError("");
    setSuccess("");

    try {
      // Get DevOps connections for this configuration
      const connectionsResponse = await settingsAPI.getDevOpsConnections(
        activeConfigurationId,
      );
      const connections = connectionsResponse.data || [];

      if (connections.length === 0) {
        setError(
          "No DevOps connections found. Please configure a DevOps connection in Settings.",
        );
        setSyncing(false);
        return;
      }

      // Sync each verified connection
      let syncedCount = 0;
      let errorCount = 0;

      for (const connection of connections) {
        if (connection.is_verified) {
          try {
            await settingsAPI.syncTasks(connection.id);
            syncedCount++;
          } catch (err) {
            console.error(`Failed to sync connection ${connection.id}:`, err);
            errorCount++;
          }
        }
      }

      if (errorCount === 0) {
        setSuccess(
          `Successfully synced ${syncedCount} connection(s) from DevOps!`,
        );
      } else {
        setSuccess(
          `Synced ${syncedCount} connection(s). ${errorCount} failed.`,
        );
        setError(`Some connections failed to sync.`);
      }

      // Reload work items after sync
      setTimeout(() => {
        loadWorkItems();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to sync from DevOps");
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmitSelected = async () => {
    if (selectedWorkItems.size === 0) {
      setError("Please select at least one work item to submit for analysis.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    const selectedItems = workItems.filter((item) =>
      selectedWorkItems.has(item.task_id),
    );
    const totalItems = selectedItems.length;

    // Set initial progress
    const progress = {
      total: totalItems,
      processed: 0,
      status: "Submitting work items for AI analysis...",
      timestamp: new Date().toISOString(),
    };
    setSubmissionProgress(progress);

    // Save to localStorage for persistence
    const submissionKey = `backlog_submission_${activeConfigurationId}`;
    localStorage.setItem(submissionKey, JSON.stringify(progress));

    const results = [];
    let successCount = 0;
    let failCount = 0;

    try {
      // Process items one at a time to show real-time progress
      for (let i = 0; i < selectedItems.length; i++) {
        const item = selectedItems[i];
        
        // Update progress for current item (i+1 because we're about to process item i+1)
        const currentProgress = {
          total: totalItems,
          processed: i + 1, // Show 1 of N when processing first item, 2 of N for second, etc.
          status: `Processing ${item.title || `item ${i + 1}`}...`,
          timestamp: new Date().toISOString(),
        };
        setSubmissionProgress(currentProgress);
        localStorage.setItem(submissionKey, JSON.stringify(currentProgress));

        try {
          // Submit single item
          const response = await dashboardAPI.submitWorkItems({
            work_items: [item],
            configuration_id: activeConfigurationId,
          });

          if (response.data.results && response.data.results.length > 0) {
            const result = response.data.results[0];
            results.push(result);
            if (result.success) {
              successCount++;
            } else {
              failCount++;
            }
          }
        } catch (itemError) {
          // Individual item failed
          results.push({
            success: false,
            title: item.title,
            error: itemError.response?.data?.error || "Failed to submit",
          });
          failCount++;
        }

        // Small delay to ensure UI updates
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Update progress to 100% completed
      const completedProgress = {
        total: totalItems,
        processed: totalItems,
        status: "Processing complete!",
        timestamp: new Date().toISOString(),
      };
      setSubmissionProgress(completedProgress);
      localStorage.setItem(submissionKey, JSON.stringify(completedProgress));

      if (failCount === 0) {
        setSuccess(
          `Successfully submitted ${successCount} work item(s) for AI analysis!`,
        );
        setSnackbarMessage(
          `Successfully submitted ${successCount} work item(s) for AI analysis!`,
        );
      } else {
        setSuccess(
          `Submitted ${successCount} work item(s) successfully. ${failCount} failed.`,
        );
        setError(
          `Some work items failed: ${results
            .filter((r) => !r.success)
            .map((r) => r.title)
            .join(", ")}`,
        );
        setSnackbarMessage(
          `Submitted ${successCount} work item(s) successfully. ${failCount} failed.`,
        );
      }
      setSnackbarOpen(true);

      // Clear selection
      setSelectedWorkItems(new Set());

      // Immediately reload work items to update submission counts and status
      await loadWorkItems();

      // Notify parent to refresh change requests if callback provided
      if (onWorkItemsSubmitted) {
        onWorkItemsSubmitted();
      }

      // Keep progress visible for 2 seconds to show 100% completion, then clear
      setTimeout(() => {
        setSubmissionProgress(null);
        localStorage.removeItem(submissionKey);
        // Reload again after progress clears to ensure UI is fully updated
        loadWorkItems();
      }, 2000);
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        "Failed to submit work items for analysis.";
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);

      // Clear submission status on error
      setSubmissionProgress(null);
      localStorage.removeItem(submissionKey);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (
      statusLower.includes("done") ||
      statusLower.includes("closed") ||
      statusLower.includes("resolved")
    ) {
      return "success";
    } else if (
      statusLower.includes("in progress") ||
      statusLower.includes("active")
    ) {
      return "info";
    } else if (statusLower.includes("new") || statusLower.includes("to do")) {
      return "default";
    } else if (
      statusLower.includes("blocked") ||
      statusLower.includes("block")
    ) {
      return "error";
    }
    return "default";
  };

  const getWorkItemTypeColor = (type) => {
    const typeLower = type?.toLowerCase() || "";
    if (typeLower.includes("user story") || typeLower.includes("story")) {
      return "primary";
    } else if (typeLower.includes("bug") || typeLower.includes("defect")) {
      return "error";
    } else if (typeLower.includes("task")) {
      return "default";
    } else if (typeLower.includes("technical debt")) {
      return "warning";
    }
    return "default";
  };

  const totalPages = Math.ceil(filteredWorkItems.length / pageSize);
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, filteredWorkItems.length);

  if (!activeConfigurationId) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a configuration to view the backlog.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" component="h2">
          DevOps Backlog
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Tooltip title="Sync latest tasks from DevOps and refresh view">
            <Button
              variant="outlined"
              startIcon={<Sync />}
              onClick={handleSyncFromDevOps}
              disabled={syncing || loading}
            >
              {syncing ? "Syncing..." : "Sync from DevOps"}
            </Button>
          </Tooltip>
          {selectedWorkItems.size > 0 && (
            <Button
              variant="contained"
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send />
                )
              }
              onClick={handleSubmitSelected}
              disabled={submitting || selectedWorkItems.size === 0}
            >
              {submitting
                ? submissionProgress
                  ? `Processing ${submissionProgress.processed}/${submissionProgress.total}...`
                  : `Submitting ${selectedWorkItems.size}...`
                : `Submit ${selectedWorkItems.size} for Analysis`}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {submissionProgress && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={
              submissionProgress.processed === submissionProgress.total
                ? "success"
                : "info"
            }
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              {submissionProgress.status}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(
                (submissionProgress.processed / submissionProgress.total) * 100,
                100,
              )}
              sx={{ height: 8, borderRadius: 1 }}
              color={
                submissionProgress.processed === submissionProgress.total
                  ? "success"
                  : "primary"
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              {submissionProgress.processed === submissionProgress.total
                ? `Completed: ${submissionProgress.processed} of ${submissionProgress.total} work items processed`
                : `Processing ${submissionProgress.processed} of ${submissionProgress.total} work items...`}
            </Typography>
          </Alert>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <TextField
        fullWidth
        placeholder="Search work items by title, description, ID, status, type, assignee, or iteration..."
        variant="outlined"
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                ×
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : workItems.length === 0 ? (
        <Alert severity={hasConnections ? "warning" : "info"}>
          {emptyMessage ||
            "No work items found. Please ensure DevOps connections are configured and verified in Settings. If connections exist, sync tasks using the sync button in Settings > DevOps Settings."}
        </Alert>
      ) : (
        <>
          {filteredWorkItems.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No work items match your search criteria. Try adjusting your
              filters or search term.
              {Object.values(columnFilters).some((f) => f) && (
                <Box mt={1}>
                  <Typography variant="body2" component="span">
                    Active filters:{" "}
                    {Object.entries(columnFilters)
                      .filter(([_, value]) => value)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(", ")}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}
          <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "fixed", width: "100%" }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{ width: "3%", padding: "4px !important" }}
                  >
                    <Checkbox
                      indeterminate={
                        selectedWorkItems.size > 0 &&
                        selectedWorkItems.size < paginatedWorkItems.length
                      }
                      checked={
                        paginatedWorkItems.length > 0 &&
                        selectedWorkItems.size === paginatedWorkItems.length
                      }
                      onChange={handleSelectAll}
                      size="small"
                      disabled={filteredWorkItems.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "7%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.id}
                      value={columnFilters.id}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("id", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="ID"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "8%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.type}
                      value={columnFilters.type}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("type", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "25%", padding: "4px !important" }}>
                    Title
                  </TableCell>
                  <TableCell sx={{ width: "10%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.status}
                      value={columnFilters.status}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("status", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            excludeClosedRemoved && !columnFilters.status
                              ? "Status (Closed/Removed hidden)"
                              : "Status"
                          }
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "10%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.assigned_to}
                      value={columnFilters.assigned_to}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("assigned_to", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Assignee"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "10%", padding: "4px !important" }}>
                    Estimates
                  </TableCell>
                  <TableCell sx={{ width: "12%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.iteration}
                      value={columnFilters.iteration}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("iteration", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Iteration"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "12%", padding: "4px !important" }}>
                    <Autocomplete
                      size="small"
                      options={filterOptions.connection}
                      value={columnFilters.connection}
                      onChange={(event, newValue) =>
                        handleColumnFilterChange("connection", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Connection"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "0.7rem",
                              padding: "2px 4px",
                              height: "24px",
                            },
                            "& .MuiInputBase-root": {
                              height: "28px",
                            },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-input": {
                          fontSize: "0.7rem",
                        },
                      }}
                      clearOnEscape
                      ListboxProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "8%", padding: "4px !important" }}>
                    Submitted
                  </TableCell>
                  <TableCell sx={{ width: "6%", padding: "4px !important" }}>
                    Count
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorkItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ padding: "20px !important" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No items to display
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedWorkItems.map((item) => (
                    <TableRow
                      key={item.task_id}
                      hover
                      selected={selectedWorkItems.has(item.task_id)}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{ padding: "4px !important" }}
                      >
                        <Checkbox
                          checked={selectedWorkItems.has(item.task_id)}
                          onChange={() => handleSelectItem(item.task_id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {item.task_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Chip
                          label={item.work_item_type || "Task"}
                          size="small"
                          color={getWorkItemTypeColor(item.work_item_type)}
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            "& .MuiChip-label": { padding: "0 6px" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            color: "primary.main",
                            fontSize: "0.75rem",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                          onClick={() => handleTitleClick(item)}
                        >
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Chip
                          label={item.status || "Unknown"}
                          size="small"
                          color={getStatusColor(item.status)}
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            "& .MuiChip-label": { padding: "0 6px" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {item.assigned_to || "Unassigned"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {item.estimate_story_points && (
                            <Chip
                              label={`${item.estimate_story_points} SP`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.65rem",
                                height: 18,
                                "& .MuiChip-label": { padding: "0 4px" },
                              }}
                            />
                          )}
                          {item.estimate_hours && (
                            <Chip
                              label={`${item.estimate_hours}h`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.65rem",
                                height: 18,
                                "& .MuiChip-label": { padding: "0 4px" },
                              }}
                            />
                          )}
                          {item.estimate_days && (
                            <Chip
                              label={`${item.estimate_days}d`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: "0.65rem",
                                height: 18,
                                "& .MuiChip-label": { padding: "0 4px" },
                              }}
                            />
                          )}
                          {!item.estimate_story_points &&
                            !item.estimate_hours &&
                            !item.estimate_days && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                -
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {item.iteration_path
                            ? item.iteration_path.split("\\").pop()
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {item.connection_name || "Unknown"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        {item.has_submission ? (
                          <Chip
                            label="Yes"
                            size="small"
                            color="success"
                            sx={{
                              fontSize: "0.65rem",
                              height: 20,
                              "& .MuiChip-label": { padding: "0 6px" },
                            }}
                          />
                        ) : (
                          <Chip
                            label="No"
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.65rem",
                              height: 20,
                              "& .MuiChip-label": { padding: "0 6px" },
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ padding: "4px !important" }}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: "0.75rem", textAlign: "center" }}
                        >
                          {item.submission_count || 0}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredWorkItems.length > 0 && (
            <Box
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Showing {startIndex} to {endIndex} of{" "}
                  {filteredWorkItems.length} work item(s)
                  {selectedWorkItems.size > 0 &&
                    ` • ${selectedWorkItems.size} selected`}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Per Page</InputLabel>
                  <Select
                    value={pageSize}
                    label="Per Page"
                    onChange={handlePageSizeChange}
                  >
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Work Item Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Work Item Details</Typography>
            <IconButton onClick={handleCloseDetailDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedWorkItem && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Work Item ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedWorkItem.task_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedWorkItem.work_item_type || "Task"}
                      size="small"
                      color={getWorkItemTypeColor(
                        selectedWorkItem.work_item_type,
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedWorkItem.status || "Unknown"}
                      size="small"
                      color={getStatusColor(selectedWorkItem.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.assigned_to || "Unassigned"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Iteration
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.iteration_path || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Connection
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.connection_name || "Unknown"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Story Points
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.estimate_story_points || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Hours
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.estimate_hours || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Days
                  </Typography>
                  <Typography variant="body1">
                    {selectedWorkItem.estimate_days || "-"}
                  </Typography>
                </Grid>
                {selectedWorkItem.created_date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedWorkItem.created_date).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                {selectedWorkItem.changed_date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Last Changed
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedWorkItem.changed_date).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Title
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedWorkItem.title}
              </Typography>

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Description
              </Typography>
              {selectedWorkItem.description ? (
                <Box
                  sx={{
                    wordBreak: "break-word",
                    maxHeight: 400,
                    overflowY: "auto",
                    p: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    "& p": {
                      margin: "0.5em 0",
                    },
                    "& div": {
                      margin: "0.5em 0",
                    },
                    "& br": {
                      display: "block",
                      margin: "0.25em 0",
                    },
                    "& b, & strong": {
                      fontWeight: "bold",
                    },
                    "& u": {
                      textDecoration: "underline",
                    },
                    "& ul, & ol": {
                      paddingLeft: "1.5em",
                      margin: "0.5em 0",
                    },
                    "& li": {
                      margin: "0.25em 0",
                    },
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                      margin: "1em 0",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      selectedWorkItem.description,
                      selectedWorkItem.connection_id,
                    ),
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No description provided
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BacklogView;
